import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { truncate } from 'lodash';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';

import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { ButtonArrowForwardIcon } from 'assets/icons';

const MOBILE_BREADCRUMBS_AMOUNT = 2;
const TABLET_BREADCRUMBS_AMOUNT = 3;

const styles = {
  breadcrumbs: {
    '.MuiBreadcrumbs-ol': {
      flexWrap: 'nowrap',
    },
  },
  separator: ({ palette }) => ({
    color: palette.text.primary,
    height: 16,
    width: 16,
  }),
  breadcrumb: ({ breakpoints, palette }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '100px',
    paddingX: 2,
    paddingY: 1,
    color: 'text.primary',
    backgroundColor: palette.colors.lightPale,
    minWidth: 111,
    textAlign: 'center',
    lineHeight: 1,
    [breakpoints.up('md')]: {
      minWidth: 200,
    },
  }),
  breadcrumbWithoutLink: {
    cursor: 'default',
  },
};

const PageBreadcrumbs = ({ breadcrumbs }) => {
  const { isSmUp, isLgUp, getResponsiveValueForCurrent } = useBreakpointFlags();
  const breadcrumbsTitleLength = getResponsiveValueForCurrent({
    xs: 20,
    sm: 30,
    md: 25,
    lg: 30,
    xl: 30,
    xxl: 30,
  });

  const displayedBreadcrumbs = useMemo(() => {
    let breadcrumbsAmount = MOBILE_BREADCRUMBS_AMOUNT;

    if (isSmUp) {
      breadcrumbsAmount = TABLET_BREADCRUMBS_AMOUNT;
    }

    if (isLgUp) {
      breadcrumbsAmount = breadcrumbs.length;
    }

    return breadcrumbs.slice(-breadcrumbsAmount);
  }, [isSmUp, isLgUp, breadcrumbs]);

  const variant = isSmUp ? 'body2' : 'caption';

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator={<Box component={ButtonArrowForwardIcon} sx={styles.separator} />}
      className="remove_for_pdf"
      sx={styles.breadcrumbs}
    >
      {displayedBreadcrumbs.map(({ title = '', link }) => {
        const truncatedTitle = truncate(title, { length: breadcrumbsTitleLength });

        return link ? (
          <Link variant={variant} sx={styles.breadcrumb} key={title} component={RouterLink} to={link} underline="none">
            {truncatedTitle}
          </Link>
        ) : (
          <Typography
            variant={variant}
            component="span"
            sx={[styles.breadcrumb, styles.breadcrumbWithoutLink]}
            key={title}
          >
            {truncatedTitle}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};

PageBreadcrumbs.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.string)])),
      ]),
    }),
  ).isRequired,
};

export default PageBreadcrumbs;
